/**
 * Núcleo de navegação entre steps do Level Play — puro, sem React.
 *
 * Existe separado do hook por dois motivos:
 *
 * 1. É a parte do motor onde um erro passa despercebido. Um off-by-one não
 *    quebra o build nem estoura exceção: a barra de progresso anda um step
 *    fora, ou a navegação cai no step anterior, e só o olho pega. Isolado
 *    aqui, é coberto por teste.
 * 2. O vitest do projeto roda em `environment: 'node'` e só coleta
 *    `src/**\/*.test.ts` — hook e componente ficam fora do alcance. Lógica
 *    pura em módulo `.ts` é o que de fato dá para testar.
 *
 * **Convenção: todo índice aqui é base zero.** A jornada já operava assim
 * (`useLevel.ts` grava `stepIndex - 1`); o quiz gravava `currentStepIndex` em
 * base um (`context.js` grava `stepIndex`) e compensava com um `- 1` na
 * toolbar. `fromLegacyQuizIndex` é a ponte de migração dessa base.
 */

export interface StepNavigationState {
  /** Índice do step atual, base zero. */
  currentStepIndex: number
  /** Total de steps conhecidos. `0` significa lista ainda não carregada. */
  totalSteps: number
}

export type StepResolution =
  | { kind: 'step'; index: number }
  | { kind: 'finish' }
  | { kind: 'noop' }

/** Piso de preenchimento da barra, para que o primeiro step não pareça vazio. */
const PROGRESS_FLOOR_PERCENT = 10

/**
 * Próximo step, ou a tela de conclusão quando já se está no último.
 *
 * Devolve `noop` enquanto a lista de steps não existir — sem isso, uma
 * navegação disparada cedo demais avança um step fantasma.
 */
export const resolveNextStep = ({
  currentStepIndex,
  totalSteps
}: StepNavigationState): StepResolution => {
  if (!totalSteps) return { kind: 'noop' }
  if (currentStepIndex + 1 >= totalSteps) return { kind: 'finish' }

  return { kind: 'step', index: currentStepIndex + 1 }
}

/**
 * Step anterior. Nunca alcança índice negativo — o quiz alcançava, por guardar
 * em base um com um guard de base zero, e não montava step nenhum.
 */
export const resolvePrevStep = ({
  currentStepIndex,
  totalSteps
}: StepNavigationState): StepResolution => {
  if (!totalSteps) return { kind: 'noop' }
  if (currentStepIndex <= 0) return { kind: 'noop' }

  return { kind: 'step', index: currentStepIndex - 1 }
}

/**
 * Índice base zero do step que corresponde ao desafio/pergunta informado, ou
 * `-1` quando não está na lista.
 *
 * O resultado é o índice final — não precisa de ajuste pelo chamador. O quiz
 * passava este valor para uma função que esperava base um e caía um step antes.
 */
export const findStepIndexById = (
  steps: ReadonlyArray<{ activityId?: number | string }>,
  id: number | string
): number => steps.findIndex(step => step.activityId == id)

/**
 * Há alguma atividade de fato jogável na lista?
 *
 * `getSteps` **sempre** injeta o `step-initial`, mesmo quando não há atividade
 * nenhuma — então `steps.length` vale 1 com a lista vazia. Contar o tamanho para
 * decidir se há o que jogar foi o que impediu o quiz esgotado de ir para a tela
 * de conclusão: o guard achava que ainda havia step e voltava cedo, deixando o
 * participante na tela de abertura com "0 desafios" e um botão que não levava a
 * lugar nenhum.
 */
export const hasPlayableStep = (
  steps: ReadonlyArray<{ isInitial?: boolean }>
): boolean => steps.some(step => !step.isInitial)

/**
 * Índice do primeiro step jogável, ou `-1` quando só existe o step inicial.
 *
 * É o destino da retomada quando a atividade pendente **não tem step** — caso do
 * kind sem componente e do conteúdo vazio, que `resolvePlayableActivities`
 * separa. Antes disso a retomada caía no índice 0, que é a própria tela de
 * abertura: o participante clicava em "Continuar" e voltava para onde já estava.
 */
export const firstPlayableStepIndex = (
  steps: ReadonlyArray<{ isInitial?: boolean }>
): number => steps.findIndex(step => !step.isInitial)

/** Percentual de progresso, saturado em `[0, 100]` e nunca `NaN`. */
export const stepProgressPercent = ({
  currentStepIndex,
  totalSteps
}: StepNavigationState): number => {
  if (!totalSteps) return 0

  const ratio = Math.max(currentStepIndex, 0) / totalSteps

  return Math.min(ratio * 100 + PROGRESS_FLOOR_PERCENT, 100)
}

/**
 * Converte o `currentStepIndex` base um que o quiz gravava para a base zero do
 * motor.
 *
 * Ponte de migração: some da base assim que `quizzes/Show/context.js` for
 * dissolvido no motor (Fase D). Com ela, o percentual exibido pelo quiz é
 * idêntico ao de antes da unificação — é o critério de verificação de R2.
 */
export const fromLegacyQuizIndex = (legacyIndex: number): number =>
  Math.max(legacyIndex - 1, 0)

/** Número do step para exibição ao participante — base um. */
export const toDisplayStep = (currentStepIndex: number): number =>
  currentStepIndex + 1
